import { useState } from "react";
import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCurrentShop,
  getCurrentUser,
  hasPermission,
  isOwnerInShopMode,
} from "@/lib/auth";
import {
  getShopCustomers,
  createCustomer,
  updateCustomerForShop,
  deleteCustomerForShop,
  searchCustomers,
} from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Plus,
  Edit2,
  Users,
  Search,
  DollarSign,
  ShoppingBag,
  Phone,
  Mail,
  MapPin,
  User,
  Trash2,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { usePlan } from "@/hooks/usePlan";

// Tipo local para cliente
type LocalCustomer = {
  id: string;
  fullName: string;
  ci: string;
  phone: string;
  email?: string;
  address?: string;
  totalPurchases: number;
  totalSpent: string | number;
  lastPurchaseAt?: string;
  isActive: boolean;
  notes?: string;
  shopId: string;
  createdAt?: string;
  updatedAt?: string;
};

export default function ShopCustomers() {
  const queryClient = useQueryClient();
  const currentShop = getCurrentShop();
  const currentUser = getCurrentUser();

  // ============ VERIFICAR SI ES OWNER EN MODO TIENDA ============
  const isOwnerMode = isOwnerInShopMode();

  // Log para depuración
  console.log("👑 ShopCustomers - ¿Es owner en modo tienda?", isOwnerMode);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<LocalCustomer | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  // Estado para el cliente a eliminar
  const [customerToDelete, setCustomerToDelete] =
    useState<LocalCustomer | null>(null);

  // Obtener información del plan
  const { can } = usePlan();

  // Función para manejar eliminación
  const handleDeleteCustomer = (customer: LocalCustomer) => {
    // Verificar si el cliente tiene compras
    if (customer.totalPurchases > 0) {
      toast({
        title: "No se puede eliminar",
        description:
          "Este cliente tiene compras registradas. No se puede eliminar.",
        variant: "destructive",
      });
      return;
    }

    setCustomerToDelete(customer);
  };

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ["shopCustomers", currentShop?.id, searchQuery],
    queryFn: () => {
      if (!currentShop) return [];
      if (searchQuery) {
        return searchCustomers(currentShop.id, searchQuery);
      } else {
        return getShopCustomers(currentShop.id);
      }
    },
    enabled: !!currentShop && (isOwnerMode || hasPermission("cashier")), // ← MODIFICADO
    refetchInterval: 5000, // Refrescar cada 5 segundos
    refetchIntervalInBackground: true,
    staleTime: 5000,
  });

  // Mutación para eliminar cliente
  const deleteMutation = useMutation({
    mutationFn: (customerId: string) =>
      deleteCustomerForShop(currentShop!.id, customerId),
    onSuccess: () => {
      toast({
        title: "Cliente eliminado",
        description: "El cliente ha sido eliminado correctamente.",
      });
      setCustomerToDelete(null);
      queryClient.invalidateQueries({
        queryKey: ["shopCustomers", currentShop!.id],
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "No se pudo eliminar el cliente",
        variant: "destructive",
      });
    },
  });

  // ============ VERIFICAR ACCESO: OWNER TIENE ACCESO PERO SUJETO AL PLAN ============
  const hasPermissionOrOwner = isOwnerMode || hasPermission("cashier");
  const hasAccess = hasPermissionOrOwner && can.useCustomers();

  console.log("👑 ShopCustomers - Acceso:", {
    isOwnerMode,
    hasPermission: hasPermission("cashier"),
    hasPermissionOrOwner,
    canUseCustomers: can.useCustomers(),
    hasAccess,
  });

  if (!currentShop || !hasAccess) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Funcionalidad no disponible
            </h3>
            <p className="text-muted-foreground">
              {!can.useCustomers()
                ? "El módulo de clientes requiere un plan Profesional o Enterprise."
                : "No tienes permiso para gestionar clientes."}
            </p>
            {!can.useCustomers() && (
              <Button
                variant="outline"
                onClick={() => (window.location.href = "/owner/plans")}
                className="mt-4 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
              >
                Mejorar Plan
              </Button>
            )}
            {can.useCustomers() && !isOwnerMode && (
              <p className="text-sm mt-4 text-muted-foreground">
                Necesitas permisos de cajero, manager o administrador para
                gestionar clientes.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calcular estadísticas
  const activeCustomers = customers.filter((c) => c.isActive).length;
  const totalSpent = customers.reduce(
    (sum, c) => sum + Number(c.totalSpent || 0),
    0,
  );
  const totalPurchases = customers.reduce(
    (sum, c) => sum + (c.totalPurchases || 0),
    0,
  );

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Encabezado */}
      <div className="mb-8 space-y-4">
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-heading font-bold uppercase text-center sm:text-left">
            Clientes Frecuentes
          </h1>
          {isOwnerMode && (
            <Badge className="bg-purple-100 text-purple-800 border-purple-200">
              👑 Modo Owner
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground text-center sm:text-left">
          Gestiona la información de tus clientes y su historial de compras
        </p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Clientes Activos
                </p>
                <p className="text-2xl font-bold">
                  {activeCustomers}/{customers.length}
                </p>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <User className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Gastado</p>
                <p className="text-2xl font-bold">
                  ${totalSpent.toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Compras Totales</p>
                <p className="text-2xl font-bold">{totalPurchases}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <ShoppingBag className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Barra de búsqueda y botón */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, CI o teléfono..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Button onClick={() => setShowAddDialog(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Nuevo Cliente
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de clientes */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p className="text-lg">No hay clientes registrados</p>
              <p className="text-sm">Agrega tu primer cliente para comenzar</p>
              {isOwnerMode && (
                <p className="text-xs mt-4 text-purple-600 bg-purple-50 p-2 rounded inline-block">
                  👑 Como owner tienes acceso total para gestionar clientes
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>CI</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Compras</TableHead>
                    <TableHead>Total Gastado</TableHead>
                    <TableHead>Última Compra</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map((customer: LocalCustomer) => (
                    <TableRow key={customer.id}>
                      <TableCell className="font-medium">
                        <div>{customer.fullName}</div>
                        {customer.email && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Mail className="h-3 w-3" /> {customer.email}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {customer.ci}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {customer.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {customer.totalPurchases || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-bold text-primary">
                        ${Number(customer.totalSpent || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {customer.lastPurchaseAt ? (
                          new Date(customer.lastPurchaseAt).toLocaleDateString()
                        ) : (
                          <span className="text-muted-foreground">Nunca</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={customer.isActive ? "default" : "outline"}
                        >
                          {customer.isActive ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setEditingCustomer(customer)}
                            title="Editar cliente"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteCustomer(customer)}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            title="Eliminar cliente"
                            disabled={customer.totalPurchases > 0} // No eliminar si tiene compras
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogo para agregar/editar cliente */}
      <CustomerDialog
        open={showAddDialog || !!editingCustomer}
        onOpenChange={(open) => {
          if (!open) {
            setShowAddDialog(false);
            setEditingCustomer(null);
          }
        }}
        customer={editingCustomer}
        shopId={currentShop.id}
      />

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={!!customerToDelete}
        onOpenChange={(open) => !open && setCustomerToDelete(null)}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              Eliminar Cliente
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="mb-4">
              ¿Estás seguro de que quieres eliminar al cliente{" "}
              <strong>{customerToDelete?.fullName}</strong>?
            </p>

            <div className="bg-destructive/10 p-3 rounded-md mb-4">
              <p className="text-sm text-destructive font-medium">
                ⚠️ Esta acción no se puede deshacer
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                CI: {customerToDelete?.ci} • Tel: {customerToDelete?.phone}
              </p>
            </div>

            {customerToDelete && customerToDelete.totalPurchases > 0 && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-md mb-4">
                <p className="text-sm text-amber-800 font-medium">
                  ⚠️ Este cliente tiene compras registradas
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Compras: {customerToDelete.totalPurchases} • Total: $
                  {Number(customerToDelete.totalSpent || 0).toFixed(2)}
                </p>
                <p className="text-xs text-amber-700 mt-2">
                  <strong>Recomendación:</strong> En lugar de eliminar, marca al
                  cliente como "Inactivo".
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCustomerToDelete(null)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(customerToDelete!.id)}
              disabled={
                deleteMutation.isPending ||
                (customerToDelete?.totalPurchases || 0) > 0
              }
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                "Eliminar Cliente"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Componente de diálogo para agregar/editar cliente
function CustomerDialog({
  open,
  onOpenChange,
  customer,
  shopId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer?: LocalCustomer | null;
  shopId: string;
}) {
  const queryClient = useQueryClient();
  const isEditing = !!customer;

  const [formData, setFormData] = useState({
    fullName: customer?.fullName || "",
    ci: customer?.ci || "",
    phone: customer?.phone || "",
    email: customer?.email || "",
    address: customer?.address || "",
    notes: customer?.notes || "",
    isActive: customer?.isActive ?? true,
  });

  // ACTUALIZAR EL ESTADO CUANDO CAMBIA EL CLIENTE
  useEffect(() => {
    if (customer) {
      setFormData({
        fullName: customer.fullName || "",
        ci: customer.ci || "",
        phone: customer.phone || "",
        email: customer.email || "",
        address: customer.address || "",
        notes: customer.notes || "",
        isActive: customer.isActive ?? true,
      });
    } else {
      // Resetear a valores vacíos si no hay cliente (para nuevo cliente)
      setFormData({
        fullName: "",
        ci: "",
        phone: "",
        email: "",
        address: "",
        notes: "",
        isActive: true,
      });
    }
  }, [customer]); // <-- Se ejecuta cada vez que cambia 'customer'

  const createMutation = useMutation({
    mutationFn: () =>
      createCustomer({
        shopId,
        fullName: formData.fullName,
        ci: formData.ci,
        phone: formData.phone,
        email: formData.email || undefined,
        address: formData.address || undefined,
        notes: formData.notes || undefined,
      }),
    onSuccess: () => {
      toast({
        title: "Cliente creado",
        description: "El cliente ha sido registrado correctamente.",
      });
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["shopCustomers", shopId] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "No se pudo crear el cliente",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateCustomerForShop(shopId, customer!.id, {
        fullName: formData.fullName,
        phone: formData.phone,
        email: formData.email || undefined,
        address: formData.address || undefined,
        notes: formData.notes || undefined,
        isActive: formData.isActive,
      }),
    onSuccess: () => {
      toast({
        title: "Cliente actualizado",
        description: "Los cambios se han guardado correctamente.",
      });
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["shopCustomers", shopId] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "No se pudo actualizar el cliente",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    // Validaciones básicas
    if (!formData.fullName || !formData.ci || !formData.phone) {
      toast({
        title: "Error",
        description: "Nombre, CI y teléfono son requeridos",
        variant: "destructive",
      });
      return;
    }

    // Validar longitud del CI (11 dígitos para Cuba)
    if (formData.ci.length !== 11) {
      toast({
        title: "CI inválido",
        description: "El Carnet de Identidad debe tener exactamente 11 dígitos",
        variant: "destructive",
      });
      return;
    }

    // Validar longitud del teléfono (mínimo 8 dígitos numéricos)
    const phoneDigits = formData.phone.replace(/[^0-9]/g, "");
    if (phoneDigits.length < 8) {
      toast({
        title: "Teléfono inválido",
        description: "El teléfono debe tener al menos 8 dígitos",
        variant: "destructive",
      });
      return;
    }

    if (isEditing) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? `Editar Cliente: ${customer?.fullName}`
              : "Registrar Nuevo Cliente"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="fullName">Nombre Completo *</Label>
            <Input
              id="fullName"
              value={formData.fullName}
              onChange={(e) =>
                setFormData({ ...formData, fullName: e.target.value })
              }
              placeholder="Ej: Juan Pérez López"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="ci">Carnet de Identidad *</Label>
              <Input
                id="ci"
                value={formData.ci}
                onChange={(e) => {
                  // Solo permitir números
                  const numericValue = e.target.value.replace(/[^0-9]/g, "");
                  setFormData({ ...formData, ci: numericValue });
                }}
                placeholder="11 dígitos (Ej: 12345678901)"
                disabled={isEditing}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={11} // Máximo 11 dígitos para CI cubano
              />
              {formData.ci && formData.ci.length < 11 && (
                <p className="text-xs text-amber-600 mt-1">
                  El CI debe tener 11 dígitos
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="phone">Teléfono *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => {
                  let value = e.target.value;

                  // Permitir el signo + solo al inicio
                  if (value.startsWith("+")) {
                    const numbers = value.substring(1).replace(/[^0-9]/g, "");
                    value = "+" + numbers;
                  } else {
                    value = value.replace(/[^0-9]/g, "");
                  }

                  setFormData({ ...formData, phone: value });
                }}
                placeholder="Ej: 54882577 o +5354882577"
                inputMode="tel"
                pattern="[0-9+]*"
              />
              {formData.phone && (
                <p className="text-xs text-gray-500 mt-1">
                  Formato: 54882577 o +5354882577
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              placeholder="cliente@ejemplo.com"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="address">Dirección</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                placeholder="Calle 123, Ciudad"
                className="pl-9"
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="notes">Notas</Label>
            <Input
              id="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Información adicional sobre el cliente"
            />
          </div>

          {isEditing && (
            <div className="grid gap-2">
              <Label htmlFor="isActive">Estado</Label>
              <select
                id="isActive"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={formData.isActive ? "active" : "inactive"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isActive: e.target.value === "active",
                  })
                }
              >
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              createMutation.isPending ||
              updateMutation.isPending ||
              !formData.fullName ||
              !formData.ci ||
              formData.ci.length !== 11 || // Validar 11 dígitos para CI
              !formData.phone ||
              formData.phone.replace(/[^0-9]/g, "").length < 8 // Mínimo 8 dígitos numéricos
            }
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : isEditing ? (
              "Guardar Cambios"
            ) : (
              "Registrar Cliente"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
